import { createStore } from "vuex";
import { auth } from "@/firebase";
import {
  addDoc,
  deleteDoc,
  getDoc,
  getDocs,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  runTransaction,
  limit,
  query,
  where,
} from "@/services/legacyBrowserPersistenceBoundary";
import { signInWithEmailAndPassword, signOut } from "firebase/auth"; // Add this line
import {
  isIdentityAuthorityReadsFeatureEnabled,
  isOrgContextFeatureEnabled,
  isOrgPermissionReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { identityAuthorityService } from "@/services/identityAuthorityService";
import { canonicalProfilePublicIdentifier } from "@/utils/profilePublicIdentifier";
import { organisationContextService } from "@/services/organisationContextService";
import { walletSummaryService } from "@/services/walletSummaryService";
import { assertLegacyMonetaryClientAvailable } from "@/services/legacyMonetaryBoundary";

import messages from "../../locales/loadLocale.js";

// modules
import userActions from "./modules/userActions";
import customOrgs, {
  listOwnPrivateOrganisationContexts,
} from "./modules/customOrgs";
import extraOrgActions from "./modules/extraOrgActions";
import productActions from "./modules/productActions";
import serviceActions from "./modules/serviceActions";
import UIState from "./modules/UIState";
import coinCreationActions from "./modules/coinCreationActions";
import { coinSymbolStates } from "./modules/coinSymbolStates";
import mediaActions from "./modules/mediaActions";
import multimediaActions from "./modules/multimediaActions";
import uploadQueue from "./modules/uploadQueue";
import appearance from "./modules/appearance";

const credentialUid = (userCredentials) => {
  const uid = userCredentials?.data?.uid;
  return typeof uid === "string" && uid.length > 0 ? uid : null;
};

const membershipFetches = new Map();
const membershipFetchErrors = new Map();
let ownIdentityFetch = null;
const selectedOrgStorageKey = (uid) => `orus.selectedOrgUID.${uid}`;
const recentOrgStorageKey = (uid) => `orus.recentOrgUIDs.${uid}`;
const PRIVATE_MEMBERSHIP_PAGE_SIZE = 50;
// Must match the canonical server bound. Production has one independently
// verified controller account with 27 memberships.
const MAX_PRIVATE_MEMBERSHIPS = 50;
const MAX_PRIVATE_MEMBERSHIP_PAGES = Math.ceil(
  MAX_PRIVATE_MEMBERSHIPS / PRIVATE_MEMBERSHIP_PAGE_SIZE,
);
const SELECTED_ORGANISATION_PERMISSION_KEYS = Object.freeze([
  "canManageOrganisation",
  "canManageMembers",
  "canManageRoles",
  "canManageAppearance",
  "canModerateItems",
  "canManageWallet",
  "canManageMonetaryPolicy",
  "canManageSiblingRelationships",
  "canManageApiKeys",
]);

const readLocalValue = (key, fallback = null) => {
  try {
    const value = window.localStorage.getItem(key);
    return value == null ? fallback : JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
};

const writeLocalValue = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Selection persistence is an enhancement, never an authority source.
  }
};

const timestampMillis = (value) => {
  if (value === null || value === undefined) return Number.MAX_SAFE_INTEGER;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const ownsPrivateMembershipRequest = ({ actor, state, userUID, generation }) =>
  auth?.currentUser === actor &&
  actor?.uid === userUID &&
  credentialUid(state.userCredentials) === userUID &&
  state.userUnitsPendingUID === userUID &&
  state.userUnitsRequestGeneration === generation;

// The own public identifier is generation-guarded like memberships: a late
// getOwnAccount response from a previous account can never publish its
// identifier for the account that is signed in now.
const clearOwnIdentityState = (state) => {
  state.ownPublicIdentifier = null;
  state.ownPublicIdentifierOwnerUID = null;
  state.ownIdentityPendingUID = null;
  state.ownIdentityRequestGeneration =
    (Number.isSafeInteger(state.ownIdentityRequestGeneration)
      ? state.ownIdentityRequestGeneration
      : 0) + 1;
};

const ownsOwnIdentityRequest = ({ actor, state, userUID, generation }) =>
  auth?.currentUser === actor &&
  actor?.uid === userUID &&
  credentialUid(state.userCredentials) === userUID &&
  state.ownIdentityPendingUID === userUID &&
  state.ownIdentityRequestGeneration === generation;

const privateContextToMembership = (context) => ({
  organisationUID: context.orgUID,
  organisationName: context.name,
  monetary_parameters: {
    coin_name: context.currencyName,
    coin_symbol: context.currency,
  },
  avatarAssetId: context.avatarAssetId,
  isActiveMember: true,
  membershipStatus: "active",
});

const loadPrivateMemberships = async ({ actor, state, userUID, generation }) => {
  const memberships = {};
  const seenCursors = new Set();
  let cursor = null;

  for (let page = 0; page < MAX_PRIVATE_MEMBERSHIP_PAGES; page += 1) {
    if (!ownsPrivateMembershipRequest({ actor, state, userUID, generation })) {
      return null;
    }
    const response = await listOwnPrivateOrganisationContexts({
      cursor,
      search: "",
      recentOrgUIDs: [],
    });
    if (!ownsPrivateMembershipRequest({ actor, state, userUID, generation })) {
      return null;
    }

    for (const context of response.contexts) {
      if (Object.prototype.hasOwnProperty.call(memberships, context.orgUID)) {
        const error = new Error("The organisation context pages overlap");
        error.code = "invalid-membership-pagination";
        throw error;
      }
      memberships[context.orgUID] = privateContextToMembership(context);
    }
    if (Object.keys(memberships).length > MAX_PRIVATE_MEMBERSHIPS) {
      const error = new Error("The account has too many organisation contexts");
      error.code = "membership-page-limit-exceeded";
      throw error;
    }

    if (!response.hasMore) return memberships;
    if (!response.nextCursor || seenCursors.has(response.nextCursor)) {
      const error = new Error("The organisation context cursor did not advance");
      error.code = "invalid-membership-pagination";
      throw error;
    }
    seenCursors.add(response.nextCursor);
    cursor = response.nextCursor;
  }

  const error = new Error("The account has too many organisation contexts");
  error.code = "membership-page-limit-exceeded";
  throw error;
};

const deterministicMembershipIds = (memberships) =>
  Object.entries(memberships || {})
    .filter(
      ([, membership]) =>
        membership?.isActiveMember !== false &&
        membership?.membershipStatus !== "inactive",
    )
    .sort(([leftId, left], [rightId, right]) => {
      const timeDifference =
        timestampMillis(left?.joined || left?.joinedUnix) -
        timestampMillis(right?.joined || right?.joinedUnix);
      return timeDifference || leftId.localeCompare(rightId);
    })
    .map(([orgId]) => orgId);

const freezeSelectedOrganisationPermissions = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const prototype = Object.getPrototypeOf(value);
  const ownKeys = Reflect.ownKeys(value);
  if (
    (prototype !== Object.prototype && prototype !== null) ||
    ownKeys.length !== SELECTED_ORGANISATION_PERMISSION_KEYS.length ||
    ownKeys.some(
      (key) =>
        typeof key !== "string" ||
        !SELECTED_ORGANISATION_PERMISSION_KEYS.includes(key) ||
        typeof value[key] !== "boolean",
    )
  ) {
    return null;
  }
  return Object.freeze(
    Object.fromEntries(
      SELECTED_ORGANISATION_PERMISSION_KEYS.map((key) => [key, value[key]]),
    ),
  );
};

const clearSelectedOrganisationPermissionsState = (state) => {
  state.selectedOrganisationPermissionsRequestGeneration =
    (Number.isSafeInteger(
      state.selectedOrganisationPermissionsRequestGeneration,
    )
      ? state.selectedOrganisationPermissionsRequestGeneration
      : 0) + 1;
  state.selectedOrganisationPermissions = null;
  state.selectedOrganisationPermissionsOwnerUID = null;
  state.selectedOrganisationPermissionsOrgUID = null;
  state.selectedOrganisationPermissionsStatus = "idle";
};

const ownsActiveOrganisationMembership = (state, userUID, orgUID) => {
  const membership = state.userUnitsData?.[orgUID];
  return (
    typeof userUID === "string" &&
    userUID.length > 0 &&
    typeof orgUID === "string" &&
    orgUID.length > 0 &&
    state.userUnitsOwnerUID === userUID &&
    Object.prototype.hasOwnProperty.call(state.userUnitsData || {}, orgUID) &&
    membership?.isActiveMember !== false &&
    membership?.membershipStatus !== "inactive"
  );
};

const ownsSelectedOrganisationPermissionState = (state) => {
  const userUID = credentialUid(state.userCredentials);
  const orgUID = state.selectedOrgUID;
  return (
    state.selectedOrgOwnerUID === userUID &&
    state.selectedOrganisationPermissionsOwnerUID === userUID &&
    state.selectedOrganisationPermissionsOrgUID === orgUID &&
    ownsActiveOrganisationMembership(state, userUID, orgUID)
  );
};

const MAX_ACTOR_ROLE_LINKS = 25;
const isRecord = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const actorRoleLinkMatchesOrganisation = ({
  roleId,
  roleLink,
  orgUID,
} = {}) =>
  typeof roleId === "string" &&
  roleId.length > 0 &&
  isRecord(roleLink) &&
  roleLink.organisationUID === orgUID &&
  (roleLink.currentRole === undefined || roleLink.currentRole === roleId);

export const canonicalRoleMatchesActor = ({
  roleId,
  role,
  userUID,
} = {}) =>
  isRecord(role) &&
  (role.roleUID === undefined || role.roleUID === roleId) &&
  isRecord(role.filledBy) &&
  Object.prototype.hasOwnProperty.call(role.filledBy, userUID);

const invalidateUserUnitsOwnership = (state) => {
  state.userUnitsOwnerUID = null;
  state.userUnitsPendingUID = null;
  state.userUnitsRequestGeneration += 1;
};

const clearUserUnitsOwnershipState = (state) => {
  state.userUnitsData = {};
  invalidateUserUnitsOwnership(state);
  clearSelectedOrganisationPermissionsState(state);
  state.currentOrgUID = null;
  state.currentOrgName = null;
  state.selectedOrgUID = null;
  state.selectedOrgOwnerUID = null;
  state.selectedOrgReady = false;
};

export const rootStoreOptions = {
  // user login and out from the firebase auth object
  state: {
    // user states
    user: {
      loggedIn: false,
      data: null,
    },
    userCredentials: {
      data: null,
    },
    // the actual Units stored in user firestore node (the UNits the user is a member of)
    userUnitsData: {},
    // Identifies which authenticated user the membership projection belongs to.
    // Pending requests are generation-guarded so an older account cannot overwrite
    // a newer account's memberships after an auth transition.
    userUnitsOwnerUID: null,
    userUnitsPendingUID: null,
    userUnitsRequestGeneration: 0,
    // The signed-in account's canonical profile identifier, tagged with the
    // UID it belongs to. Profile links must use this, never the UID.
    ownPublicIdentifier: null,
    ownPublicIdentifierOwnerUID: null,
    ownIdentityPendingUID: null,
    ownIdentityRequestGeneration: 0,
    // user Roles
    userRoles: null,
    // user's role privieges when viewing a role, used in the roleView.vue
    userRolePrivileges: null,
    userRolePrivilegesOwnerUID: null,
    userRolePrivilegesOrgUID: null,
    userRolePrivilegesRequestGeneration: 0,
    userRolePrivilegesStatus: "idle",

    // ORGANISATIONS
    // current user organisation
    currentOrgUID: null,
    currentOrgName: null,
    selectedOrgUID: null,
    selectedOrgOwnerUID: null,
    selectedOrgReady: false,
    recentOrgUIDs: [],
    selectedOrganisationPermissions: null,
    selectedOrganisationPermissionsOwnerUID: null,
    selectedOrganisationPermissionsOrgUID: null,
    selectedOrganisationPermissionsRequestGeneration: 0,
    selectedOrganisationPermissionsStatus: "idle",
    // UI_STATE
    showLoginModal: false,
    showLoginModalNoRedirect: false,
    loginPromptReason: null,
    showRegisterModal: false,
    showRegisterModalNoRedirect: false,
    menuOpen: false,
    selectedOrg: null,
    activeTab: null,
    isLoading: false,
    navbarVisible: true,
    inviteStatus: null,
  },
  getters: {
    inviteStatus: (state) => state.inviteStatus,
    // getting user object values
    user: (state) => state.user,
    userCredentials: (state) => state.userCredentials,
    userUnitsData: (state) => state.userUnitsData,
    userUnitsOwnerUID: (state) => state.userUnitsOwnerUID,
    userUnitsDataForUid: (state) => (userUID) =>
      typeof userUID === "string" && state.userUnitsOwnerUID === userUID
        ? state.userUnitsData
        : {},
    ownPublicIdentifier: (state) => {
      const userUID = credentialUid(state.userCredentials);
      return userUID && state.ownPublicIdentifierOwnerUID === userUID
        ? state.ownPublicIdentifier
        : null;
    },
    ownPublicIdentifierForUid: (state) => (userUID) =>
      typeof userUID === "string" &&
      userUID.length > 0 &&
      state.ownPublicIdentifierOwnerUID === userUID &&
      credentialUid(state.userCredentials) === userUID
        ? state.ownPublicIdentifier
        : null,

    userRoles: (state) => state.userRoles,

    // getting user's role privieges when viewing a role, used in the roleView.vue
    userRolePrivileges: (state) =>
      state.userRolePrivilegesStatus === "ready" &&
      state.userRolePrivilegesOwnerUID === state.selectedOrgOwnerUID &&
      state.userRolePrivilegesOrgUID === state.selectedOrgUID
        ? state.userRolePrivileges
        : null,

    // getting the users' only organisation
    currentUserOrgName: (state) => {
      if (state.currentOrgName) {
        return state.currentOrgName; // assuming organisationName is a field in  userUnitsData
      }
      return null;
    },
    currentOrgUID: (state) => state.currentOrgUID,
    selectedOrgUID: (state) => state.selectedOrgUID,
    selectedOrgOwnerUID: (state) => state.selectedOrgOwnerUID,
    selectedOrgReady: (state) => state.selectedOrgReady,
    selectedOrgMembership: (state) =>
      state.selectedOrgUID ? state.userUnitsData[state.selectedOrgUID] || null : null,
    activeSelectedOrg: (state, getters) => getters.selectedOrgMembership,
    userOrgIds: (state) => deterministicMembershipIds(state.userUnitsData),
    hasMultipleOrgs: (_state, getters) => getters.userOrgIds.length > 1,
    recentOrgUIDs: (state) => state.recentOrgUIDs,
    selectedOrganisationPermissions: (state) =>
      state.selectedOrganisationPermissionsStatus === "ready" &&
      ownsSelectedOrganisationPermissionState(state)
        ? state.selectedOrganisationPermissions
        : null,
    selectedOrganisationPermissionsStatus: (state) =>
      ownsSelectedOrganisationPermissionState(state)
        ? state.selectedOrganisationPermissionsStatus
        : "idle",
    userMembershipsReadyForUid: (state) => (userUID) =>
      typeof userUID === "string" &&
      state.userUnitsOwnerUID === userUID &&
      state.userUnitsPendingUID === null,
    // I'm not sure this is being used anywhere in the app
    currentUserOrgId: (state) => state.selectedOrgUID,
    // UI_STATE
    showLoginModal: (state) => state.showLoginModal,
    loginPromptReason: (state) => state.loginPromptReason,
    showLoginModalNoRedirect: (state) => state.showLoginModalNoRedirect,
    showRegisterModalNoRedirect: (state) => state.showRegisterModalNoRedirect,
    showRegisterModal: (state) => state.showRegisterModal,
    menuOpen: (state) => state.menuOpen,
    // creating a new org
    selectedOrg: (state) => state.selectedOrg,
    activeTab: (state) => state.activeTab,
    // loadin animation
    isLoading: (state) => state.isLoading,
  },
  mutations: {
    // set user data objects
    SET_USER(state, data) {
      state.user.data = data;
    },
    SET_LOGGED_IN(state, value) {
      state.user.loggedIn = value;
    },
    SET_USER_CREDENTIALS(state, userCredentials) {
      const previousUid = credentialUid(state.userCredentials);
      if (userCredentials && userCredentials.data) {
        state.userCredentials = userCredentials;
      } else if (userCredentials && userCredentials.user) {
        state.userCredentials = {
          data: userCredentials.user,
        };
      } else {
        state.userCredentials = { data: userCredentials };
      }
      if (previousUid !== credentialUid(state.userCredentials)) {
        clearUserUnitsOwnershipState(state);
        clearOwnIdentityState(state);
        state.userRoles = null;
        state.userRolePrivileges = null;
        state.userRolePrivilegesOwnerUID = null;
        state.userRolePrivilegesOrgUID = null;
        state.userRolePrivilegesRequestGeneration =
          (Number.isSafeInteger(state.userRolePrivilegesRequestGeneration)
            ? state.userRolePrivilegesRequestGeneration
            : 0) + 1;
        state.userRolePrivilegesStatus = "idle";
      }
      // console.log("SET_USER_CREDENTIALS");
      // console.log(state.userCredentials);
    },
    // set actual data object on firebase and store the user credential auth object
    SET_USER_UNITS(state, userUnitsData) {
      state.userUnitsData = userUnitsData;
      // Legacy unscoped writes do not carry the source UID. Invalidate the owner
      // tag so Account cannot present this data as another authenticated user's.
      invalidateUserUnitsOwnership(state);
      clearSelectedOrganisationPermissionsState(state);
    },
    BEGIN_USER_UNITS_FETCH(state, userUID) {
      state.userUnitsRequestGeneration += 1;
      state.userUnitsPendingUID = userUID;

      if (state.userUnitsOwnerUID !== userUID) {
        state.userUnitsData = {};
        state.userUnitsOwnerUID = null;
        clearSelectedOrganisationPermissionsState(state);
        state.currentOrgUID = null;
        state.currentOrgName = null;
      }
    },
    COMPLETE_USER_UNITS_FETCH(
      state,
      { userUID, generation, userUnitsData }
    ) {
      if (
        state.userUnitsPendingUID !== userUID ||
        state.userUnitsRequestGeneration !== generation
      ) {
        return;
      }

      state.userUnitsData = userUnitsData;
      state.userUnitsOwnerUID = userUID;
      state.userUnitsPendingUID = null;
      if (
        state.selectedOrganisationPermissionsOwnerUID !== null &&
        !ownsActiveOrganisationMembership(
          state,
          state.selectedOrganisationPermissionsOwnerUID,
          state.selectedOrganisationPermissionsOrgUID,
        )
      ) {
        clearSelectedOrganisationPermissionsState(state);
      }
    },
    UPSERT_USER_UNIT(state, { userUID, orgId, membership }) {
      if (
        typeof userUID !== "string" ||
        !userUID ||
        typeof orgId !== "string" ||
        !orgId ||
        !membership
      ) {
        return;
      }
      if (state.userUnitsOwnerUID && state.userUnitsOwnerUID !== userUID) return;
      state.userUnitsData = {
        ...(state.userUnitsOwnerUID === userUID ? state.userUnitsData : {}),
        [orgId]: membership,
      };
      state.userUnitsOwnerUID = userUID;
      state.userUnitsPendingUID = null;
    },
    FAIL_USER_UNITS_FETCH(state, { userUID, generation }) {
      if (
        state.userUnitsPendingUID === userUID &&
        state.userUnitsRequestGeneration === generation
      ) {
        state.userUnitsPendingUID = null;
      }
    },
    CLEAR_USER_UNITS(state) {
      clearUserUnitsOwnershipState(state);
    },
    BEGIN_OWN_IDENTITY_FETCH(state, userUID) {
      state.ownIdentityRequestGeneration =
        (Number.isSafeInteger(state.ownIdentityRequestGeneration)
          ? state.ownIdentityRequestGeneration
          : 0) + 1;
      state.ownIdentityPendingUID = userUID;
      if (state.ownPublicIdentifierOwnerUID !== userUID) {
        state.ownPublicIdentifier = null;
        state.ownPublicIdentifierOwnerUID = null;
      }
    },
    COMPLETE_OWN_IDENTITY_FETCH(
      state,
      { userUID, generation, publicIdentifier }
    ) {
      if (
        state.ownIdentityPendingUID !== userUID ||
        state.ownIdentityRequestGeneration !== generation ||
        credentialUid(state.userCredentials) !== userUID ||
        canonicalProfilePublicIdentifier(publicIdentifier) === null
      ) {
        return;
      }
      state.ownPublicIdentifier = publicIdentifier;
      state.ownPublicIdentifierOwnerUID = userUID;
      state.ownIdentityPendingUID = null;
    },
    FAIL_OWN_IDENTITY_FETCH(state, { userUID, generation }) {
      if (
        state.ownIdentityPendingUID === userUID &&
        state.ownIdentityRequestGeneration === generation
      ) {
        state.ownIdentityPendingUID = null;
      }
    },
    CLEAR_OWN_IDENTITY(state) {
      clearOwnIdentityState(state);
    },
    SET_USER_ROLES(state, userRolesData) {
      state.userRoles = userRolesData;
    },

    // ORGANISATIONS
    SET_CURRENT_ORG_UID(state, orgUID) {
      state.currentOrgUID = orgUID;
    },
    SET_SELECTED_ORG_CONTEXT(state, { userUID, orgUID, recentOrgUIDs = [] }) {
      if (
        state.selectedOrgOwnerUID !== (userUID || null) ||
        state.selectedOrgUID !== (orgUID || null)
      ) {
        clearSelectedOrganisationPermissionsState(state);
      }
      state.selectedOrgOwnerUID = userUID || null;
      state.selectedOrgUID = orgUID || null;
      state.selectedOrgReady = true;
      state.currentOrgUID = orgUID || null;
      state.currentOrgName = orgUID
        ? state.userUnitsData[orgUID]?.organisationName || null
        : null;
      state.recentOrgUIDs = recentOrgUIDs;
    },
    CLEAR_SELECTED_ORG_CONTEXT(state) {
      clearSelectedOrganisationPermissionsState(state);
      state.selectedOrgUID = null;
      state.selectedOrgOwnerUID = null;
      state.selectedOrgReady = false;
      state.recentOrgUIDs = [];
      state.currentOrgUID = null;
      state.currentOrgName = null;
    },
    // cretaing a new org
    SET_SELECTED_ORG(state, org) {
      state.selectedOrg = org;
    },
    SET_CURRENT_ORG_NAME(state, orgName) {
      state.currentOrgName = orgName;
    },

    // NAVIGATION & UI STATE
    SET_ACTIVE_TAB(state, tabName) {
      state.activeTab = tabName;
    },
    TOGGLE_LOGIN_MODAL(state) {
      state.showLoginModal = !state.showLoginModal;
      if (!state.showLoginModal) state.loginPromptReason = null;
    },
    HIDE_LOGIN_MODAL(state) {
      state.showLoginModal = false;
      state.loginPromptReason = null;
    },
    SET_LOGIN_PROMPT_REASON(state, reason) {
      state.loginPromptReason = reason;
    },
    TOGGLE_LOGIN_MODAL_NO_REDIRECT(state) {
      state.showLoginModalNoRedirect = !state.showLoginModalNoRedirect;
    },
    TOGGLE_REGISTER_MODAL(state) {
      state.showRegisterModal = !state.showRegisterModal;
    },
    TOGGLE_REGISTER_MODAL_NO_REDIRECT(state) {
      state.showRegisterModalNoRedirect = !state.showRegisterModalNoRedirect;
    },
    SET_MENU_OPEN(state, payload) {
      state.menuOpen = payload;
    },

    // LOADING loading state for app wide use
    SET_LOADING_STATE(state, status) {
      state.isLoading = status;
    },
    // setting user's role privieges when view a role, used in the roleView.vue
    SET_ROLE_PRIVILEGES(state, userRolePrivileges) {
      state.userRolePrivileges = userRolePrivileges;
    },
    BEGIN_USER_ROLE_PRIVILEGES(state, { userUID, orgUID }) {
      state.userRolePrivilegesRequestGeneration =
        (Number.isSafeInteger(state.userRolePrivilegesRequestGeneration)
          ? state.userRolePrivilegesRequestGeneration
          : 0) + 1;
      state.userRolePrivilegesOwnerUID = userUID;
      state.userRolePrivilegesOrgUID = orgUID;
      state.userRolePrivilegesStatus = "pending";
      state.userRolePrivileges = null;
    },
    COMPLETE_USER_ROLE_PRIVILEGES(
      state,
      { userUID, orgUID, generation, privileges },
    ) {
      if (
        state.userRolePrivilegesOwnerUID !== userUID ||
        state.userRolePrivilegesOrgUID !== orgUID ||
        state.userRolePrivilegesRequestGeneration !== generation
      ) {
        return;
      }
      state.userRolePrivileges = privileges;
      state.userRolePrivilegesStatus = "ready";
    },
    FAIL_USER_ROLE_PRIVILEGES(state, { userUID, orgUID, generation }) {
      if (
        state.userRolePrivilegesOwnerUID !== userUID ||
        state.userRolePrivilegesOrgUID !== orgUID ||
        state.userRolePrivilegesRequestGeneration !== generation
      ) {
        return;
      }
      state.userRolePrivileges = null;
      state.userRolePrivilegesStatus = "error";
    },
    // clearing user's role privieges when view a role, used in the roleView.vue
    CLEAR_USER_ROLE_PRIVILEGES(state) {
      state.userRolePrivilegesRequestGeneration =
        (Number.isSafeInteger(state.userRolePrivilegesRequestGeneration)
          ? state.userRolePrivilegesRequestGeneration
          : 0) + 1;
      state.userRolePrivileges = null;
      state.userRolePrivilegesOwnerUID = null;
      state.userRolePrivilegesOrgUID = null;
      state.userRolePrivilegesStatus = "idle";
    },
    BEGIN_SELECTED_ORGANISATION_PERMISSIONS(state, { userUID, orgUID }) {
      state.selectedOrganisationPermissionsRequestGeneration =
        (Number.isSafeInteger(
          state.selectedOrganisationPermissionsRequestGeneration,
        )
          ? state.selectedOrganisationPermissionsRequestGeneration
          : 0) + 1;
      state.selectedOrganisationPermissions = null;
      state.selectedOrganisationPermissionsOwnerUID = userUID;
      state.selectedOrganisationPermissionsOrgUID = orgUID;
      state.selectedOrganisationPermissionsStatus = "pending";
    },
    COMPLETE_SELECTED_ORGANISATION_PERMISSIONS(
      state,
      { userUID, orgUID, generation, permissions },
    ) {
      if (
        state.selectedOrganisationPermissionsOwnerUID !== userUID ||
        state.selectedOrganisationPermissionsOrgUID !== orgUID ||
        state.selectedOrganisationPermissionsRequestGeneration !== generation
      ) {
        return;
      }
      const normalizedPermissions =
        freezeSelectedOrganisationPermissions(permissions);
      if (!normalizedPermissions) {
        state.selectedOrganisationPermissions = null;
        state.selectedOrganisationPermissionsStatus = "error";
        return;
      }
      state.selectedOrganisationPermissions = normalizedPermissions;
      state.selectedOrganisationPermissionsStatus = "ready";
    },
    FAIL_SELECTED_ORGANISATION_PERMISSIONS(
      state,
      { userUID, orgUID, generation },
    ) {
      if (
        state.selectedOrganisationPermissionsOwnerUID !== userUID ||
        state.selectedOrganisationPermissionsOrgUID !== orgUID ||
        state.selectedOrganisationPermissionsRequestGeneration !== generation
      ) {
        return;
      }
      state.selectedOrganisationPermissions = null;
      state.selectedOrganisationPermissionsStatus = "error";
    },
    CLEAR_SELECTED_ORGANISATION_PERMISSIONS(state) {
      clearSelectedOrganisationPermissionsState(state);
    },
    setNavbarVisibility(state, isVisible) {
      state.navbarVisible = isVisible;
    },
    SET_INVITE_STATUS(state, status) {
      state.inviteStatus = status;
    },
  },

  actions: {
    // Compatibility actions to support both namespaced and non-namespaced access
    // These are needed until all component uses are updated
    async addMedia({ dispatch }, payload) {
      try {
        console.log(
          "Root store: Forwarding addMedia call to mediaActions module"
        );
        return await dispatch("mediaActions/addMediaPost", payload);
      } catch (error) {
        console.error("Error in root store addMedia:", error);
        throw error;
      }
    },

    async updateMedia({ dispatch }, payload) {
      try {
        console.log(
          "Root store: Forwarding updateMedia call to mediaActions module"
        );
        return await dispatch("mediaActions/updateMediaItem", payload);
      } catch (error) {
        console.error("Error in root store updateMedia:", error);
        throw error;
      }
    },

    async uploadMediaFile({ dispatch }, payload) {
      try {
        console.log(
          "Root store: Forwarding uploadMediaFile call to mediaActions module"
        );
        return await dispatch("mediaActions/uploadMediaFile", payload);
      } catch (error) {
        console.error("Error in root store uploadMediaFile:", error);
        // Fall back to directly importing and using the utility
        const { uploadMediaFile } = await import("@/utils/uploadUtils.js");
        return await uploadMediaFile(payload.file);
      }
    },

    async fetchOrgDetails(context, orgUID) {
      assertLegacyMonetaryClientAvailable();
      try {
        if (!orgUID) {
          console.error("fetchOrgDetails: No orgUID provided");
          return null;
        }

        // Get the custom org details
        const orgRef = doc(null, "custom-orgs", orgUID);
        const orgDoc = await getDoc(orgRef);

        if (!orgDoc.exists()) {
          console.error(`Organization ${orgUID} not found`);
          return null;
        }

        const orgData = orgDoc.data();
        return {
          membersCount: orgData.memberCount || orgData.members?.length || 1,
          totalOrgCoins: orgData.totalOrgCoins || 1,
          monetary_parameters: orgData.monetary_parameters || {},
        };
      } catch (error) {
        console.error("Error fetching org details:", error);
        return null;
      }
    },

    async logInEmailPassword(context, { email, password }) {
      const response = await signInWithEmailAndPassword(auth, email, password);
      if (response) {
        await context.dispatch("logInStateCommit", response);
        return response;
      } else {
        throw new Error("Login failed");
      }
    },
    async logInStateCommit(context, response) {
      context.commit("SET_LOADING_STATE", true);
      const nextUid = response?.user?.uid || response?.data?.uid || null;
      if (credentialUid(context.state.userCredentials) !== nextUid) {
        walletSummaryService.clearCache();
        membershipFetches.clear();
        membershipFetchErrors.clear();
        ownIdentityFetch = null;
        await context.dispatch("UIState/resetForAuthChange", null, {
          root: true,
        });
        context.commit("SET_USER_UNITS_CUSTOM_ORGS", null);
      }
      // Publish credential ownership before reactive consumers see the user.
      context.commit("SET_USER_CREDENTIALS", response);
      context.commit("SET_USER", response.user);
      context.commit("SET_LOGGED_IN", true);
      context.commit("HIDE_LOGIN_MODAL");
      context.commit("SET_LOADING_STATE", false);
    },
    async logOutStateCommit(context) {
      walletSummaryService.clearCache();
      membershipFetches.clear();
      membershipFetchErrors.clear();
      ownIdentityFetch = null;
      context.commit("SET_LOADING_STATE", true);
      context.commit("CLEAR_SELECTED_ORGANISATION_PERMISSIONS");
      // Sign-out forgets the own profile identifier before any await so a
      // signed-out shell never renders a link to the previous account.
      context.commit("CLEAR_OWN_IDENTITY");
      await context.dispatch("UIState/resetEconomyRestrictions");
      await context.dispatch("UIState/resetForAuthChange");
      await context.dispatch("clearLogoContext");
      await context.dispatch("appearance/clearForAuthChange", null, { root: true });
      await signOut(auth);
      context.commit("SET_USER", null);
      context.commit("SET_LOGGED_IN", false);
      context.commit("SET_USER_CREDENTIALS", null);
      context.commit("CLEAR_USER_UNITS");
      context.commit("SET_USER_UNITS_CUSTOM_ORGS", null);
      context.commit("SET_LOADING_STATE", false);
    },
    async fetchUser({ commit }, user) {
      commit("SET_LOADING_STATE", true);

      if (user) {
        commit("SET_USER", {
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
          uid: user.uid,
          emailVerified: user.emailVerified,
        });
        commit("SET_LOGGED_IN", true);
        commit("SET_LOADING_STATE", false);
      } else {
        commit("SET_USER", null);
        commit("SET_LOGGED_IN", false);
        commit("SET_LOADING_STATE", false);
      }
    },
    async fetchUserCredentials({ commit }, userCredentials) {
      commit("SET_LOADING_STATE", true);
      if (userCredentials) {
        commit("SET_USER_CREDENTIALS", userCredentials);
        commit("SET_LOADING_STATE", false);
      } else {
        commit("SET_USER_CREDENTIALS", null);
        commit("SET_LOADING_STATE", false);
      }
    },

    // Resolve the signed-in account's canonical public identifier once per
    // account. Every link to the user's own profile reads it from here; a
    // Firebase UID is never a profile route parameter.
    async ensureOwnPublicIdentifier({ commit, state }, { userUID, force = false } = {}) {
      if (
        !isIdentityAuthorityReadsFeatureEnabled ||
        typeof userUID !== "string" ||
        userUID.length === 0
      ) {
        return null;
      }
      const actor = auth?.currentUser || null;
      if (
        actor?.uid !== userUID ||
        credentialUid(state.userCredentials) !== userUID
      ) {
        return null;
      }
      if (
        !force &&
        state.ownPublicIdentifierOwnerUID === userUID &&
        state.ownPublicIdentifier
      ) {
        return state.ownPublicIdentifier;
      }
      if (
        !force &&
        ownIdentityFetch?.uid === userUID &&
        ownIdentityFetch.generation === state.ownIdentityRequestGeneration &&
        state.ownIdentityPendingUID === userUID
      ) {
        return ownIdentityFetch.promise;
      }

      commit("BEGIN_OWN_IDENTITY_FETCH", userUID);
      const generation = state.ownIdentityRequestGeneration;
      const request = Object.freeze({ actor, state, userUID, generation });
      const promise = (async () => {
        try {
          const ownAccount = await identityAuthorityService.getOwnAccount();
          if (!ownsOwnIdentityRequest(request)) return null;
          const publicIdentifier = canonicalProfilePublicIdentifier(
            ownAccount?.publicIdentifier
          );
          if (!publicIdentifier) {
            commit("FAIL_OWN_IDENTITY_FETCH", { userUID, generation });
            return null;
          }
          commit("COMPLETE_OWN_IDENTITY_FETCH", {
            userUID,
            generation,
            publicIdentifier,
          });
          return publicIdentifier;
        } catch (_error) {
          // Profile links stay absent; the identity is still shown unlinked.
          if (ownsOwnIdentityRequest(request)) {
            commit("FAIL_OWN_IDENTITY_FETCH", { userUID, generation });
          }
          return null;
        }
      })();
      ownIdentityFetch = Object.freeze({ uid: userUID, generation, promise });
      return promise;
    },

    // fetch user data from Firestore database user node units sub-collection (all the info we have been recording about the user)
    async fetchAndCommitUserUnits({ commit, dispatch, state }, userUID) {
      if (typeof userUID !== "string" || userUID.length === 0) {
        commit("CLEAR_USER_UNITS");
        return null;
      }
      const privateActor = isOrgContextFeatureEnabled ? auth?.currentUser : null;
      if (
        isOrgContextFeatureEnabled &&
        (privateActor?.uid !== userUID ||
          credentialUid(state.userCredentials) !== userUID)
      ) {
        return null;
      }

      const previousOrganisationIds =
        state.userUnitsOwnerUID === userUID
          ? Object.keys(state.userUnitsData || {})
          : [];
      commit("BEGIN_USER_UNITS_FETCH", userUID);
      membershipFetchErrors.delete(userUID);
      const generation = state.userUnitsRequestGeneration;
      commit("SET_LOADING_STATE", true);
      try {
        let userUnitsData;
        if (isOrgContextFeatureEnabled) {
          userUnitsData = await loadPrivateMemberships({
            actor: privateActor,
            state,
            userUID,
            generation,
          });
          if (userUnitsData === null) {
            if (
              state.userUnitsPendingUID === userUID &&
              state.userUnitsRequestGeneration === generation
            ) {
              commit("FAIL_USER_UNITS_FETCH", { userUID, generation });
              commit("SET_LOADING_STATE", false);
            }
            return null;
          }
        } else {
          const userUnitsRef = collection(null, "users", userUID, "Units");
          const querySnapshot = await getDocs(userUnitsRef);
          userUnitsData = {};
          querySnapshot.forEach((doc) => {
            userUnitsData[doc.id] = doc.data();
          });
        }

        if (
          state.userUnitsPendingUID !== userUID ||
          state.userUnitsRequestGeneration !== generation
        ) {
          return null;
        }

        commit("COMPLETE_USER_UNITS_FETCH", {
          userUID,
          generation,
          userUnitsData,
        });
        membershipFetchErrors.delete(userUID);
        const removedOrganisationIds = previousOrganisationIds.filter(
          (orgId) => !Object.prototype.hasOwnProperty.call(userUnitsData, orgId),
        );
        await Promise.all(
          removedOrganisationIds.map((orgId) =>
            dispatch("appearance/forgetOrganisation", { uid: userUID, orgId }),
          ),
        );
        commit("SET_LOADING_STATE", false);
        // console.log("Fetched user units data:", userUnitsData);
        return userUnitsData;
      } catch (error) {
        if (
          state.userUnitsPendingUID === userUID &&
          state.userUnitsRequestGeneration === generation
        ) {
          commit("FAIL_USER_UNITS_FETCH", { userUID, generation });
          commit("SET_LOADING_STATE", false);
          membershipFetchErrors.set(userUID, error);
        }
        return null;
      }
    },

    async ensureUserMemberships({ dispatch, getters }, { userUID, force = false }) {
      if (typeof userUID !== "string" || !userUID) return {};
      if (!force && getters.userMembershipsReadyForUid(userUID)) {
        return getters.userUnitsDataForUid(userUID);
      }
      if (!force && membershipFetches.has(userUID)) {
        return membershipFetches.get(userUID);
      }
      const request = dispatch("fetchAndCommitUserUnits", userUID).then((result) => {
        if (result === null && !getters.userMembershipsReadyForUid(userUID)) {
          const error = membershipFetchErrors.get(userUID) ||
            Object.assign(new Error("Memberships could not be loaded"), {
              code: "membership-fetch-failed",
            });
          membershipFetchErrors.delete(userUID);
          throw error;
        }
        return result || getters.userUnitsDataForUid(userUID) || {};
      });
      membershipFetches.set(userUID, request);
      try {
        return await request;
      } finally {
        if (membershipFetches.get(userUID) === request) {
          membershipFetches.delete(userUID);
        }
      }
    },

    setSelectedOrgUID({ commit, state, dispatch }, { userUID, orgUID }) {
      const memberships = state.userUnitsOwnerUID === userUID ? state.userUnitsData : {};
      const membership = memberships[orgUID];
      if (
        !orgUID ||
        !Object.prototype.hasOwnProperty.call(memberships, orgUID) ||
        membership?.isActiveMember === false ||
        membership?.membershipStatus === "inactive"
      ) {
        const error = new Error("The selected organisation is not an active membership");
        error.code = "invalid-selected-org";
        throw error;
      }
      const storedRecent = readLocalValue(recentOrgStorageKey(userUID), []);
      if (
        state.selectedOrgOwnerUID === userUID &&
        state.selectedOrgUID &&
        state.selectedOrgUID !== orgUID
      ) {
        walletSummaryService.clearCache();
        dispatch?.("UIState/resetForContextChange", null, { root: true });
      }
      const recentOrgUIDs = [orgUID, ...(Array.isArray(storedRecent) ? storedRecent : [])]
        .filter((value, index, values) =>
          values.indexOf(value) === index &&
          memberships[value]?.isActiveMember !== false &&
          memberships[value]?.membershipStatus !== "inactive",
        )
        .slice(0, 5);
      writeLocalValue(selectedOrgStorageKey(userUID), orgUID);
      writeLocalValue(recentOrgStorageKey(userUID), recentOrgUIDs);
      commit("SET_SELECTED_ORG_CONTEXT", { userUID, orgUID, recentOrgUIDs });
      commit("CLEAR_USER_ROLE_PRIVILEGES");
      return orgUID;
    },

    reconcileCreatedOrganisation({ commit, dispatch, state }, { userUID, context }) {
      if (
        credentialUid(state.userCredentials) !== userUID ||
        !context ||
        context.membershipStatus !== "active"
      ) {
        const error = new Error("The created membership does not belong to this account");
        error.code = "invalid-created-membership";
        throw error;
      }
      const orgId = context.orgUID;
      commit("UPSERT_USER_UNIT", {
        userUID,
        orgId,
        membership: {
          organisationUID: orgId,
          organisationName: context.name,
          monetary_parameters: {
            coin_name: context.currency?.name || "",
            coin_symbol: context.currency?.symbol || "",
          },
          isActiveMember: true,
          membershipStatus: "active",
        },
      });
      return dispatch("setSelectedOrgUID", { userUID, orgUID: orgId });
    },

    async ensureSelectedOrg({ dispatch, commit, state }, { userUID, force = false }) {
      const memberships = await dispatch("ensureUserMemberships", { userUID, force });
      const orderedIds = deterministicMembershipIds(memberships);
      if (orderedIds.length === 0) {
        commit("SET_SELECTED_ORG_CONTEXT", { userUID, orgUID: null, recentOrgUIDs: [] });
        return null;
      }
      const storedOrgUID = readLocalValue(selectedOrgStorageKey(userUID));
      const currentIsOwned =
        state.selectedOrgOwnerUID === userUID &&
        Object.prototype.hasOwnProperty.call(
          memberships,
          state.selectedOrgUID,
        ) &&
        memberships[state.selectedOrgUID]?.isActiveMember !== false &&
        memberships[state.selectedOrgUID]?.membershipStatus !== "inactive";
      const selectedOrgUID = currentIsOwned
        ? state.selectedOrgUID
        : orderedIds.includes(storedOrgUID)
          ? storedOrgUID
          : orderedIds[0];
      return dispatch("setSelectedOrgUID", { userUID, orgUID: selectedOrgUID });
    },

    clearSelectedOrg({ commit }) {
      commit("CLEAR_SELECTED_ORG_CONTEXT");
    },

    async fetchSelectedOrganisationPermissions(
      { commit, state },
      { orgUID } = {},
    ) {
      if (
        !isOrgContextFeatureEnabled ||
        !isOrgPermissionReadsFeatureEnabled
      ) {
        commit("CLEAR_SELECTED_ORGANISATION_PERMISSIONS");
        return null;
      }

      const actor = auth?.currentUser;
      const userUID = credentialUid(state.userCredentials);
      if (
        !actor ||
        actor.uid !== userUID ||
        state.selectedOrgOwnerUID !== userUID ||
        state.selectedOrgUID !== orgUID ||
        !ownsActiveOrganisationMembership(state, userUID, orgUID)
      ) {
        commit("CLEAR_SELECTED_ORGANISATION_PERMISSIONS");
        return null;
      }

      commit("BEGIN_SELECTED_ORGANISATION_PERMISSIONS", { userUID, orgUID });
      const generation =
        state.selectedOrganisationPermissionsRequestGeneration;
      const ownsRequest = () =>
        auth?.currentUser === actor &&
        actor.uid === userUID &&
        credentialUid(state.userCredentials) === userUID &&
        state.selectedOrgOwnerUID === userUID &&
        state.selectedOrgUID === orgUID &&
        state.selectedOrganisationPermissionsOwnerUID === userUID &&
        state.selectedOrganisationPermissionsOrgUID === orgUID &&
        state.selectedOrganisationPermissionsRequestGeneration === generation &&
        ownsActiveOrganisationMembership(state, userUID, orgUID);

      try {
        const context =
          await organisationContextService.getOwnOrganisationContext(orgUID);
        if (!ownsRequest()) return null;
        const permissions = freezeSelectedOrganisationPermissions(
          context?.permissions,
        );
        if (context?.orgUID !== orgUID || !permissions) {
          commit("FAIL_SELECTED_ORGANISATION_PERMISSIONS", {
            userUID,
            orgUID,
            generation,
          });
          return null;
        }
        commit("COMPLETE_SELECTED_ORGANISATION_PERMISSIONS", {
          userUID,
          orgUID,
          generation,
          permissions,
        });
        return state.selectedOrganisationPermissions;
      } catch (_error) {
        if (ownsRequest()) {
          commit("FAIL_SELECTED_ORGANISATION_PERMISSIONS", {
            userUID,
            orgUID,
            generation,
          });
        }
        return null;
      }
    },

    clearSelectedOrganisationPermissions({ commit }) {
      commit("CLEAR_SELECTED_ORGANISATION_PERMISSIONS");
    },

    async fetchAndCommitUserRoles({ commit }, userId) {
      if (isOrgPermissionReadsFeatureEnabled) {
        commit("SET_USER_ROLES", {});
        return null;
      }
      if (!userId) {
        console.log("userId is not provided");
        return;
      }
      const userRolesCollectionRef = collection(null, "users", userId, "RoleIDs");
      const querySnapshot = await getDocs(userRolesCollectionRef);
      const userRolesData = {};
      querySnapshot.forEach((doc) => {
        userRolesData[doc.id] = doc.data();
      });
      commit("SET_USER_ROLES", userRolesData);
    },

    async fetchUserRolesAndPrivileges({ commit, getters }, userId) {
      if (isOrgPermissionReadsFeatureEnabled) {
        commit("SET_USER_ROLES", {});
        commit("SET_ROLE_PRIVILEGES", {});
        return null;
      }
      try {
        // Fetch user roles
        const userRolesCollectionRef = collection(
          null,
          "users",
          userId,
          "RoleIDs"
        );
        const userRolesSnapshot = await getDocs(userRolesCollectionRef);
        const userRolesData = {};
        const userPrivileges = {};

        // Fetch privileges for each role
        for (const roleDoc of userRolesSnapshot.docs) {
          const roleId = roleDoc.id;
          const roleData = roleDoc.data();
          userRolesData[roleId] = roleData;

          // Now fetch the privileges associated with this role
          const privilegesDocRef = doc(
            null,
            "custom-orgs",
            getters.currentOrgUID,
            "Roles",
            roleId
          );
          const privilegesDocSnapshot = await getDoc(privilegesDocRef);

          if (privilegesDocSnapshot.exists()) {
            userPrivileges[roleId] =
              privilegesDocSnapshot.data().privileges || {};
          }
        }

        // Commit roles and privileges to the store
        commit("SET_USER_ROLES", userRolesData);
        commit("SET_ROLE_PRIVILEGES", userPrivileges);
      } catch (error) {
        console.error("Error fetching user roles and privileges:", error);
        commit("SET_USER_ROLES", {});
        commit("SET_ROLE_PRIVILEGES", {});
      }
    },

    // UI_STATE
    changeActiveTab({ commit }, tabName) {
      commit("SET_ACTIVE_TAB", tabName);
    },
    toggleLoginModal({ commit }) {
      commit("TOGGLE_LOGIN_MODAL");
    },
    toggleLoginModalWithReason({ commit }, reason) {
      commit("SET_LOGIN_PROMPT_REASON", reason);
      commit("TOGGLE_LOGIN_MODAL");
    },
    toggleLoginModalNoRedirect({ commit }) {
      commit("TOGGLE_LOGIN_MODAL_NO_REDIRECT");
    },
    toggleRegisterModal({ commit }) {
      commit("TOGGLE_REGISTER_MODAL");
    },
    toggleRegisterModalNoRedirect({ commit }) {
      commit("TOGGLE_REGISTER_MODAL_NO_REDIRECT");
    },

    // creating a new org
    setSelectedOrg({ commit }, org) {
      commit("SET_SELECTED_ORG", org);
    },

    // getting user's role privileges when viewing another role, used in the roleView.vue
    // something is not right about this i'm getting the privleges from the role the user is currently viewing, that's not what i want, i want to get the privieges from current user role
    async getUserRolePrivileges(
      { commit, dispatch, getters, state },
      { orgUID },
    ) {

      // Guard: orgUID may be undefined during initial mount
      if (!orgUID || typeof orgUID !== "string") {
        commit("CLEAR_USER_ROLE_PRIVILEGES");
        return;
      }

      // Raw RoleIDs/Roles documents are not a permission presentation API.
      // Until the bounded capability read is enabled, fail closed instead of
      // reconstructing privileges from browser-readable legacy collections.
      if (!isOrgPermissionReadsFeatureEnabled) {
        commit("CLEAR_USER_ROLE_PRIVILEGES");
        return null;
      }

      if (isOrgPermissionReadsFeatureEnabled) {
        commit("CLEAR_USER_ROLE_PRIVILEGES");
        return dispatch("fetchSelectedOrganisationPermissions", { orgUID });
      }

      if (
        getters.userCredentials &&
        getters.userCredentials.data &&
        getters.userCredentials.data.uid
      ) {
        const userUID = getters.userCredentials.data.uid; // Get the authenticated user's UID
        if (
          state.selectedOrgOwnerUID !== userUID ||
          state.selectedOrgUID !== orgUID
        ) {
          commit("CLEAR_USER_ROLE_PRIVILEGES");
          return null;
        }
        commit("BEGIN_USER_ROLE_PRIVILEGES", { userUID, orgUID });
        const generation = state.userRolePrivilegesRequestGeneration;
        const ownsContext = () =>
          state.userCredentials?.data?.uid === userUID &&
          state.selectedOrgOwnerUID === userUID &&
          state.selectedOrgUID === orgUID;

        try {
          const userRoleIDsCollectionRef = collection(
            null,
            "users",
            userUID,
            "RoleIDs"
          );
          const userRoleIDsQuerySnapshot = await getDocs(
            query(
              userRoleIDsCollectionRef,
              where("organisationUID", "==", orgUID),
              limit(MAX_ACTOR_ROLE_LINKS + 1),
            ),
          );
          const matchingRoleLinks = userRoleIDsQuerySnapshot.docs.filter(
            (roleLinkSnapshot) =>
              roleLinkSnapshot.exists() &&
              actorRoleLinkMatchesOrganisation({
                roleId: roleLinkSnapshot.id,
                roleLink: roleLinkSnapshot.data(),
                orgUID,
              }),
          );
          if (matchingRoleLinks.length > MAX_ACTOR_ROLE_LINKS) {
            throw new Error("The membership has too many role links");
          }
          let userRolePrivileges = {}; // Initialize the privileges object

          for (const roleLinkSnapshot of matchingRoleLinks) {
            if (!ownsContext()) return null;
            const userRoleID = roleLinkSnapshot.id;
            const roleDocRef = doc(
              null,
              "custom-orgs",
              orgUID,
              "Roles",
              userRoleID
            );
            const roleDocSnapshot = await getDoc(roleDocRef);

            if (roleDocSnapshot.exists()) {
              const roleData = roleDocSnapshot.data();
              if (
                canonicalRoleMatchesActor({
                  roleId: userRoleID,
                  role: roleData,
                  userUID,
                })
              ) {
                userRolePrivileges = {
                  ...userRolePrivileges,
                  ...roleData.privileges,
                };
              }
            }
          }

          if (!ownsContext()) return null;
          commit("COMPLETE_USER_ROLE_PRIVILEGES", {
            userUID,
            orgUID,
            generation,
            privileges: userRolePrivileges,
          });
          return userRolePrivileges;
        } catch (error) {
          console.error("Error fetching user role privileges:", error);
          commit("FAIL_USER_ROLE_PRIVILEGES", {
            userUID,
            orgUID,
            generation,
          });
          return null;
        }
      } else {
        commit("CLEAR_USER_ROLE_PRIVILEGES");
        return null;
      }
    },

    toggleNavbarVisibility({ commit }, isVisible) {
      commit("setNavbarVisibility", isVisible);
    },
    setLoadingState({ commit }, isLoading) {
      commit("SET_LOADING_STATE", isLoading);
    },
  },
  modules: {
    userActions,
    customOrgs,
    extraOrgActions,
    productActions,
    serviceActions,
    UIState,
    coinCreationActions,
    coinSymbolStates,
    mediaActions,
    multimediaActions,
    uploadQueue,
    appearance,
  },
};

export default createStore(rootStoreOptions);
